import { WarningCircle } from '@phosphor-icons/react'
import { Button } from 'antd'
import { useEffect, useState } from 'react'

import { DATE_CONSTANTS } from '@/domain/constants/date'
import { useTrackUserInactivity } from '@/hooks/useTrackUserInactivity'
import Modal from '@/ui/Modal'

type ModalContentProps = {
  onConfirm: () => void
  onInactivityDetected: () => void
  onReturnToStart: () => void
  modalSecondsLeft: number
}

const ModalContent = ({
  onConfirm,
  onInactivityDetected,
  onReturnToStart,
  modalSecondsLeft,
}: ModalContentProps) => {
  const [seconds, setSeconds] = useState(modalSecondsLeft)

  useEffect(() => {
    if (seconds === 0) {
      onInactivityDetected()
      return
    }
    const intervalId = setInterval(() => {
      setSeconds((old) => old - 1)
    }, DATE_CONSTANTS.TIME_IN_MILLISECONDS.SECOND * 1)
    return () => clearInterval(intervalId)
  }, [onInactivityDetected, seconds])
  return (
    <Modal
      centered
      open
      footer={() => undefined}
      onCancel={onConfirm}
      width={548}
    >
      <div className="flex w-full flex-col items-center justify-start">
        <WarningCircle
          className="mt-3 rounded-full border-2 border-yellow-400/80 text-7xl text-yellow-400/80"
          weight="fill"
        />
        <h1 className="mt-4 text-2xl font-bold">Are you still here?</h1>
        <p className="mt-1 text-base">This form will close out in</p>
        <strong className="my-4 text-2xl font-bold">{seconds}s</strong>
        <div className="my-1 flex w-full items-center justify-center gap-4">
          <Button onClick={onReturnToStart} type="default">
            Return to Start
          </Button>
          <Button
            className="mb-1 animate-shake"
            onClick={onConfirm}
            type="primary"
          >
            I'm Still Here
          </Button>
        </div>
      </div>
    </Modal>
  )
}

type Props = {
  modalSecondsLeft?: number
  inactiveSeconds?: number
  onInactivityDetected: () => void
  onReturnToStart: () => void
}

export const ModalAreYouStillHere = ({
  modalSecondsLeft = 10,
  inactiveSeconds = 30,
  onInactivityDetected,
  onReturnToStart,
}: Props) => {
  const { resetInactivityCheck, isInactive } = useTrackUserInactivity({
    inactiveSeconds,
  })

  return isInactive ? (
    <ModalContent
      {...{
        modalSecondsLeft,
        onConfirm: resetInactivityCheck,
        onInactivityDetected,
        onReturnToStart,
      }}
    />
  ) : null
}
